export type FilterType = "original" | "auto" | "bw" | "highContrast";

interface FilterOptions {
  brightness: number; // -100 to 100
  contrast: number;   // -100 to 100
  grayscale: boolean;
}

const filterPresets: Record<FilterType, FilterOptions> = {
  original: { brightness: 0, contrast: 0, grayscale: false },
  auto: { brightness: 10, contrast: 30, grayscale: false },
  bw: { brightness: 5, contrast: 40, grayscale: true },
  highContrast: { brightness: 15, contrast: 60, grayscale: false },
};

export function getFilterLabel(filter: FilterType): string {
  switch (filter) {
    case "original": return "Original";
    case "auto": return "Auto";
    case "bw": return "N&B";
    case "highContrast": return "Contraste+";
  }
}

export function applyFilter(imageData: ImageData, filter: FilterType): ImageData {
  const options = filterPresets[filter];
  const data = new Uint8ClampedArray(imageData.data);
  
  // Brightness factor: -100 to 100 -> -255 to 255
  const brightnessFactor = (options.brightness / 100) * 255;
  
  // Contrast factor: -100 to 100 -> 0.5 to 2.0
  const contrastFactor = (options.contrast + 100) / 100;
  
  for (let i = 0; i < data.length; i += 4) {
    let r = data[i];
    let g = data[i + 1];
    let b = data[i + 2];
    
    // Apply brightness
    r = r + brightnessFactor;
    g = g + brightnessFactor;
    b = b + brightnessFactor;
    
    // Apply contrast (centered at 128)
    r = ((r - 128) * contrastFactor) + 128;
    g = ((g - 128) * contrastFactor) + 128;
    b = ((b - 128) * contrastFactor) + 128;
    
    // Convert to grayscale if needed
    if (options.grayscale) {
      const gray = 0.299 * r + 0.587 * g + 0.114 * b;
      r = g = b = gray;
    }
    
    // Clamp values
    data[i] = Math.max(0, Math.min(255, r));
    data[i + 1] = Math.max(0, Math.min(255, g));
    data[i + 2] = Math.max(0, Math.min(255, b));
  }
  
  return new ImageData(data, imageData.width, imageData.height);
}

// Apply filter to canvas for preview
export function applyFilterToCanvas(
  ctx: CanvasRenderingContext2D,
  img: HTMLImageElement,
  filter: FilterType,
  x: number,
  y: number,
  width: number,
  height: number
): void {
  // Draw original image
  ctx.drawImage(img, x, y, width, height);
  
  if (filter === "original") return;
  
  // Get image data and apply filter
  const imageData = ctx.getImageData(x, y, width, height);
  const filtered = applyFilter(imageData, filter);
  ctx.putImageData(filtered, x, y);
}

// Apply filter to a blob and return new blob
export async function applyFilterToBlob(blob: Blob, filter: FilterType): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext("2d");
      
      if (!ctx) {
        reject(new Error("Could not get canvas context"));
        return;
      }
      
      ctx.drawImage(img, 0, 0);
      
      if (filter !== "original") {
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const filtered = applyFilter(imageData, filter);
        ctx.putImageData(filtered, 0, 0);
      }
      
      canvas.toBlob(
        (result) => {
          if (result) {
            resolve(result);
          } else {
            reject(new Error("Failed to create blob"));
          }
        },
        "image/jpeg",
        0.92
      );
    };
    img.onerror = reject;
    img.src = URL.createObjectURL(blob);
  });
}
